import {
  parseCsvPlayers,
  type Entrant,
  type PlayerInfo,
  type Sex,
  type Title,
  type TournamentEventBody,
} from '@ctd/core';
import { newId } from './ids';

export const TITLES: readonly Title[] = ['GM', 'IM', 'FM', 'CM', 'WGM', 'WIM', 'WFM', 'WCM', 'HM'];

export interface PlayerForm {
  name: string;
  rating: string;
  title: Title | '';
  federation: string;
  fideId: string;
  birthDate: string;
  sex: Sex | '';
  club: string;
}

export function emptyPlayerForm(): PlayerForm {
  return {
    name: '',
    rating: '',
    title: '',
    federation: '',
    fideId: '',
    birthDate: '',
    sex: '',
    club: '',
  };
}

export function formFromPlayer(p: Entrant): PlayerForm {
  return {
    name: p.name,
    rating: p.rating > 0 ? String(p.rating) : '',
    title: p.title ?? '',
    federation: p.federation ?? '',
    fideId: p.fideId ?? '',
    birthDate: p.birthDate ?? '',
    sex: p.sex ?? '',
    club: p.club ?? '',
  };
}

export type PlayerFormErrors = Partial<Record<keyof PlayerForm, string>>;

export interface PlayerInfoResult {
  readonly info: PlayerInfo | null;
  readonly errors: PlayerFormErrors;
}

export function playerInfoFromForm(form: PlayerForm): PlayerInfoResult {
  const errors: PlayerFormErrors = {};
  const name = form.name.trim();
  if (name === '') errors.name = 'Enter the player’s name.';
  const ratingText = form.rating.trim();
  const rating = ratingText === '' ? 0 : Number(ratingText);
  if (!Number.isInteger(rating) || rating < 0 || rating > 4000) {
    errors.rating = 'Enter a whole number from 0 to 4000, or leave it empty.';
  }
  const federation = form.federation.trim().toUpperCase();
  if (federation !== '' && !/^[A-Z]{3}$/.test(federation)) {
    errors.federation = 'Use the three-letter code, e.g. GER.';
  }
  const fideId = form.fideId.trim();
  if (fideId !== '' && !/^\d+$/.test(fideId)) errors.fideId = 'A FIDE ID has digits only.';
  if (Object.keys(errors).length > 0) return { info: null, errors };
  return {
    info: {
      name,
      rating,
      ...(rating > 0 ? { fideRating: rating } : {}),
      title: form.title,
      federation,
      fideId,
      birthDate: form.birthDate.trim(),
      sex: form.sex === '' ? null : form.sex,
      club: form.club.trim(),
    },
    errors,
  };
}

export function addPlayerEvent(info: PlayerInfo, id = newId('p')): TournamentEventBody {
  return {
    type: 'PlayerAdded',
    player: { ...info, id, withdrawnAfterRound: null, byeRequests: [] },
  };
}

/** The changed fields of a player for PlayerUpdated (empty when nothing changed). */
export function playerPatch(p: Entrant, info: PlayerInfo): Partial<PlayerInfo> {
  const patch: Record<string, unknown> = {};
  const before: Record<string, unknown> = {
    name: p.name,
    rating: p.rating,
    title: p.title ?? '',
    federation: p.federation ?? '',
    fideId: p.fideId ?? '',
    birthDate: p.birthDate ?? '',
    sex: p.sex ?? null,
    club: p.club ?? '',
  };
  for (const [k, v] of Object.entries(before)) {
    const next = (info as unknown as Record<string, unknown>)[k];
    if (next !== v) patch[k] = next;
  }
  // The form edits one rating; keep the FIDE rating in step when it followed it.
  if (patch['rating'] !== undefined && (p.fideRating === undefined || p.fideRating === p.rating)) {
    patch['fideRating'] = info.rating;
  }
  return patch as Partial<PlayerInfo>;
}

/** Player ids ordered for seeding: rating high to low, then name. */
export function seedOrder(players: readonly Entrant[]): string[] {
  return [...players]
    .sort((a, b) => b.rating - a.rating || a.name.localeCompare(b.name))
    .map((p) => p.id);
}

/** True when the players are already in seeding order. */
export function isSeeded(players: readonly Entrant[]): boolean {
  const order = seedOrder(players);
  const current = [...players].sort((a, b) => a.pairingNumber - b.pairingNumber).map((p) => p.id);
  return order.every((id, i) => current[i] === id);
}

export interface CsvPreview {
  readonly players: PlayerInfo[];
  readonly error: string | null;
}

export function previewCsv(text: string): CsvPreview {
  if (text.trim() === '') return { players: [], error: null };
  try {
    const players = parseCsvPlayers(text);
    if (players.length === 0) {
      return {
        players,
        error: 'No players found. The first line must be a header with a "name" column.',
      };
    }
    return { players, error: null };
  } catch (e) {
    return { players: [], error: e instanceof Error ? e.message : String(e) };
  }
}

export function playerStatus(p: Entrant): string {
  return p.withdrawnAfterRound === null ? '' : `Withdrawn after round ${p.withdrawnAfterRound}`;
}
